import React from "react";
import { motion } from "framer-motion";
import { Download, Play, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export function Hero() {
  return (
    <section className="relative min-h-screen flex items-center justify-center px-4 py-20 overflow-hidden">
      {/* Animated gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-transparent to-cyan-900/20"></div>

      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-2 mb-6"
            >
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-purple-300 text-sm font-medium">
                AI Destekli Fotoğraf Teknolojisi
              </span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
            >
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                Etkinliğinize
              </span>
              <br />
              <span className="text-white">Sihir Katın!</span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl text-gray-300 mb-8 leading-relaxed max-w-2xl"
            >
              Eyay Booth ile misafirleriniz saniyeler içinde AI tarafından
              oluşturulmuş muhteşem fotoğraflara sahip olur. Düğün, doğum günü,
              kurumsal etkinlik - hangi organizasyon olursa olsun, unutulmaz
              anılar yaratın!
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Button
                size="lg"
                className="group bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-purple-500/50 transition-all duration-300"
                onClick={() =>
                  document
                    .getElementById("downloads")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                <Download className="w-5 h-5 mr-2 group-hover:animate-bounce" />
                Uygulamayı İndir
              </Button>

              <Button
                size="lg"
                variant="outline"
                className="border-2 border-purple-500/50 text-white hover:bg-purple-500/10 px-8 py-6 text-lg rounded-xl backdrop-blur-sm"
              >
                <Play className="w-5 h-5 mr-2" />
                Demo İzle
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="grid grid-cols-3 gap-6 mt-12 pt-12 border-t border-gray-800"
            >
              <div className="text-center lg:text-left">
                <div className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  10K+
                </div>
                <div className="text-gray-400 text-sm mt-1">Mutlu Müşteri</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-3xl font-bold bg-gradient-to-r from-pink-400 to-cyan-400 bg-clip-text text-transparent">
                  100+
                </div>
                <div className="text-gray-400 text-sm mt-1">AI Teması</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                  5000+
                </div>
                <div className="text-gray-400 text-sm mt-1">Etkinlik</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Visual/Animation */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative hidden lg:block"
          >
            {/* Main Device Mockup */}
            <div className="relative">
              {/* Glowing effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 rounded-3xl blur-3xl opacity-30 animate-pulse"></div>

              {/* Device Frame */}
              <div className="relative bg-gray-900 rounded-3xl p-4 border border-gray-800 shadow-2xl">
                <div className="aspect-[9/16] bg-gradient-to-br from-purple-900 via-pink-900 to-cyan-900 rounded-2xl overflow-hidden">
                  {/* Simulated App Interface */}
                  <div className="p-6 h-full flex flex-col">
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center">
                        <motion.div
                          animate={{
                            scale: [1, 1.2, 1],
                            rotate: [0, 360],
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                          className="w-32 h-32 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 flex items-center justify-center"
                        >
                          <Sparkles className="w-16 h-16 text-white" />
                        </motion.div>
                        <div className="text-white text-lg font-semibold">
                          AI Sihri Çalışıyor...
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                animate={{ y: [0, -20, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute -top-8 -right-8 bg-purple-500/20 backdrop-blur-sm border border-purple-500/30 rounded-2xl px-6 py-3"
              >
                <div className="text-purple-300 text-sm font-medium">
                  ✨ Anında Sonuç
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 20, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5,
                }}
                className="absolute -bottom-8 -left-8 bg-cyan-500/20 backdrop-blur-sm border border-cyan-500/30 rounded-2xl px-6 py-3"
              >
                <div className="text-cyan-300 text-sm font-medium">
                  📸 HD Kalite
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
